import {Nullish} from 'shared/web';

import {EagGridState} from '../createEagGridStore';

/**
 * Composite selector for the drag/resize state read by every `HeaderCell`.
 * Merges 5 individual selectors (activeDragId, overColumnId, dropSide, isResizing,
 * resizingColumn) into one reference-stable object — M header cells × 5
 * subscriptions → M × 1.
 *
 * `verticalSeparatorsVisible` is intentionally excluded: it is derived from
 * `activePreset.gridSettings` (not a direct store field), changes only on preset
 * switches, and is kept as a separate subscription to avoid coupling this selector
 * to preset state.
 *
 * Returns the same object reference when all five fields are identical (===),
 * so `useSyncExternalStore` skips re-renders for header cells that did not
 * need to update.
 */
export type HeaderCellOverlayState = {
  activeDragId: string | Nullish;
  overColumnId: string | Nullish;
  dropSide: 'left' | 'right' | Nullish;
  isResizing: boolean;
  resizingColumn: string | Nullish;
};

let cached: HeaderCellOverlayState = {
  activeDragId: null,
  overColumnId: null,
  dropSide: null,
  isResizing: false,
  resizingColumn: null,
};

export const selectHeaderCellOverlayState = (state: EagGridState): HeaderCellOverlayState => {
  if (
    state.activeDragId === cached.activeDragId &&
    state.overColumnId === cached.overColumnId &&
    state.dropSide === cached.dropSide &&
    state.isResizing === cached.isResizing &&
    state.resizingColumn === cached.resizingColumn
  ) {
    return cached;
  }

  cached = {
    activeDragId: state.activeDragId,
    overColumnId: state.overColumnId,
    dropSide: state.dropSide,
    isResizing: state.isResizing,
    resizingColumn: state.resizingColumn,
  };

  return cached;
};
